import React, { useState, useMemo, useCallback, useRef } from 'react';
import FilterTemplate from '../components/filtertemplate/FilterTemplate';
import {
  fetchTestNTag,
  createTestNTag,
  updateTestNTag,
  requestDeleteAuthTestNTag,
  authoriseDeleteTestNTag,
  downloadTestNTagExcel,
} from '../utils/api';
import { cssUrl } from '../utils/cssVersion';
import HelpButton from '../components/shared/HelpButton';
import CustomSelect from '../components/shared/CustomSelect';
import ReassigningModalTestnTag from '../components/ReassigningModalTestnTag';
import { useUser } from '../utils/UserContext';

const filterTemplateStyles = cssUrl('filterTemplate.css');

/* ── Field indices (matches get_testNtag_data row order) ── */
const F = {
  id: 0,
  leadStatus: 1,   // Ok / Overdue
  unitStatus: 2,   // Ok / Overdue
  leadDue: 3,      // lead due date
  unitDue: 4,      // unit due date
  client: 5,       // site
  licence: 6,      // user who created the record
  item: 7,
  itemCode: 8,
  condition: 9,    // Good / Bad / Repair
  licenceNumber: 10,
  leadTag: 11,
  unitTag: 12,
  serialNum: 13,
  inspectLead: 14,
  inspectUnit: 15,
  passFail: 16,
  comments: 17,    // free-text repair / inspection notes
  authDelete: 18,  // "Authorise Delete" — 6 digit code while a delete is pending
  pendingFlag: 19, // synthetic column added in fetchData: 'PendingDelete' when authDelete holds a 6-digit code
};

const ITEM_CODES = ['UNIT_EL', 'UNIT_EU', 'EL', 'EU', 'UNIT', 'Appliance', 'PB', 'NO_Appliance', 'Repair', 'Off_Site'];
const CONDITIONS = ['Good', 'Bad', 'Repair'];
const PASS_FAIL = ['Pass', 'Fail'];

const REPAIR = 'Repair';
const OK = 'Ok';
const OVERDUE = 'Overdue';
const DEPOT = 'Depot';
const PENDING = 'PendingDelete';   // synthetic chip value for records awaiting delete authorisation
const eqI = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
const isRepairCond = (v) => eqI(v, REPAIR);
const isPendingCode = (v) => /^\d{6}$/.test(String(v ?? '').trim());

const HELP = { chip: 'testntag', urlFilter: '/static/pdffilters/TestNTag.pdf' };

/* ── Item Code reference (read-only) ── */
const ITEM_CODE_INFO = [
  { name: 'UNIT_EL',      code: 'UNIT_EL',      lead: '180', unit: '365', desc: 'Unit with extension lead', num: '2' },
  { name: 'UNIT_EU',      code: 'UNIT_EU',      lead: '365', unit: '365', desc: 'Unit with unit extension or unit charger', num: '2' },
  { name: 'EL',           code: 'EL',           lead: '180', unit: '180', desc: 'Extension lead', num: '1' },
  { name: 'EU',           code: 'EU',           lead: '365', unit: '365', desc: 'Unit charger', num: '1' },
  { name: 'UNIT',         code: 'UNIT',         lead: '365', unit: '365', desc: 'Unit', num: '1' },
  { name: 'Appliance',    code: 'Appliance',    lead: '365', unit: '365', desc: 'Washing machine or Microwave etc', num: '1' },
  { name: 'PB',           code: 'PB',           lead: '180', unit: '180', desc: 'Power board', num: '1' },
  { name: 'NO_Appliance', code: 'NO_Appliance', lead: "Today's Date in Inspect Date", unit: "Today's Date in Inspect Date", desc: "Any item that doesn't have a tag", num: '1' },
  { name: 'Repair',       code: 'Repair',       lead: '0',   unit: '0',   desc: 'Equipment on repair', num: '1' },
  { name: 'Off_Site',     code: 'Off_Site',     lead: '0',   unit: '0',   desc: 'Removed from site', num: '1' },
];

/* ── Helpers ── */
const firstName = (s) => String(s ?? '').trim().split(/\s+/)[0] || '';
const dmyToISO = (s) => {
  const str = String(s ?? '').trim();
  if (!str) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const [d, m, y] = str.split('/');
  return d && m && y ? `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` : '';
};
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const S = {
  eyebrow: { fontSize: '0.62rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#22d3ee', fontWeight: 700 },
  link: { background: 'none', border: 'none', color: '#22d3ee', cursor: 'pointer', fontWeight: 600, padding: 0, marginLeft: 4 },
  blockColHead: { fontSize: '0.62rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  blockRowLabel: { fontSize: '0.85rem', fontWeight: 700, color: '#22d3ee' },
  blockValue: { fontSize: '0.86rem' },
  statusBadge: { display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700 },
  suggestWrap: { display: 'flex', gap: 6, flexWrap: 'wrap', padding: '4px 0 6px' },
  suggestPill: { background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.4)', color: '#c4b5fd', borderRadius: 999, padding: '3px 12px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' },
  /* modal */
  panelAccent: { position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, #22d3ee, #3b82f6, #8b5cf6)' },
  modalBackdrop: { position: 'fixed', inset: 0, top: 'var(--fr-modal-top, 0px)', background: 'rgba(2,6,23,0.7)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' },
  modal: { position: 'relative', overflow: 'visible', width: '100%', maxWidth: 520, background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', border: '1px solid rgba(34,211,238,0.25)', borderRadius: 16, padding: '18px 18px 12px' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  modalTitle: { margin: 0, fontSize: '1rem', fontWeight: 700, color: '#f1f5f9' },
  closeX: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '1.4rem', lineHeight: 1, cursor: 'pointer' },
  fieldLabel: { display: 'block', fontSize: '0.6rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 3 },
  input: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 12px', color: '#e2e8f0', fontSize: '0.9rem', outline: 'none' },
  textarea: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 12px', color: '#e2e8f0', fontSize: '0.9rem', outline: 'none', minHeight: 84, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4 },
  readOnly: { width: '100%', boxSizing: 'border-box', background: 'rgba(34,211,238,0.06)', border: '1px solid rgba(34,211,238,0.25)', borderRadius: 10, padding: '10px 12px', color: '#22d3ee', fontSize: '0.9rem', fontWeight: 700 },
  groupHead: { fontSize: '0.7rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#22d3ee', fontWeight: 700, margin: '12px 2px 8px' },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  submitBtn: { width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: 'linear-gradient(90deg, #22d3ee, #3b82f6)', color: '#0f172a', fontWeight: 700, cursor: 'pointer', margin: '10px 0 4px' },
  altBtn: { width: '100%', boxSizing: 'border-box', padding: '11px', borderRadius: 10, border: '1px solid rgba(139,92,246,0.45)', background: 'rgba(139,92,246,0.12)', color: '#c4b5fd', fontWeight: 700, cursor: 'pointer', margin: '10px 0 4px' },
  dangerBtn: { width: '100%', boxSizing: 'border-box', padding: '11px', borderRadius: 10, border: 'none', background: 'linear-gradient(90deg, #f87171, #ef4444)', color: '#fff', fontWeight: 700, cursor: 'pointer', margin: '10px 0 4px' },
  requestBtn: { width: '100%', boxSizing: 'border-box', padding: '11px', borderRadius: 10, border: '1px solid rgba(244,63,94,0.45)', background: 'rgba(244,63,94,0.12)', color: '#fb7185', fontWeight: 700, cursor: 'pointer', margin: '0 0 4px' },
  codeInput: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: '10px 12px', color: '#e2e8f0', fontSize: '1.05rem', outline: 'none', textAlign: 'center', letterSpacing: '0.35em', fontWeight: 700 },
  closeBtn: { width: '100%', padding: '11px', borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.06)', color: '#e2e8f0', fontWeight: 600, cursor: 'pointer', margin: '0 0 8px' },
  errText: { color: '#f87171', fontSize: '0.78rem', margin: '4px 2px 8px' },
  /* two-tier site autocomplete (new record form) */
  siteDrop: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#0f172a', border: '1px solid rgba(34,211,238,0.35)', borderRadius: 10, maxHeight: 230, overflowY: 'auto', zIndex: 1300, boxShadow: '0 12px 30px rgba(0,0,0,0.55)', WebkitOverflowScrolling: 'touch' },
  siteName: { fontSize: '0.88rem', fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  siteAddr: { fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
};

/* ── Icons ── */
const SheetIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    <path d="M8 9h2M8 13h8M8 17h8" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);
const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);
const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
/* Wrench — shown on cards whose Condition is Repair */
const WrenchIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);
/* Chip icons — Ok / Overdue / Depot */
const CheckCircleIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const ClockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);
const HomeIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
  </svg>
);
/* Hourglass — pending delete (chip + per-record action icon) */
const HourglassIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12" />
    <path d="M6 22h12" />
    <path d="M8 2v3.5L12 10l4-4.5V2" />
    <path d="M8 22v-3.5L12 14l4 4.5V22" />
  </svg>
);
/* Reassign — two people with a swap arrow */
const ReassignIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="7" cy="7" r="3" />
    <path d="M2 21v-1a5 5 0 0 1 5-5" />
    <circle cx="17" cy="7" r="3" />
    <path d="M22 21v-1a5 5 0 0 0-5-5" />
    <path d="M9 17h6" />
    <path d="M13 14l3 3-3 3" />
  </svg>
);

/* ── Small render pieces ── */
const StatusBadge = ({ status }) => {
  const ok = String(status).toLowerCase() === 'ok';
  const st = ok
    ? { background: 'rgba(34,197,94,0.15)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.4)' }
    : { background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.4)' };
  return <span style={{ ...S.statusBadge, ...st }}>{status || '—'}</span>;
};

/* Filter chip palette — Ok/Overdue mirror the StatusBadge colours */
const CHIP_COLORS = {
  repair:  { text: '#fbbf24', border: 'rgba(245,158,11,0.35)', activeBg: 'rgba(245,158,11,0.18)', activeBorder: 'rgba(245,158,11,0.55)' },
  ok:      { text: '#4ade80', border: 'rgba(34,197,94,0.35)',  activeBg: 'rgba(34,197,94,0.15)',  activeBorder: 'rgba(34,197,94,0.55)' },
  overdue: { text: '#f87171', border: 'rgba(239,68,68,0.35)',  activeBg: 'rgba(239,68,68,0.15)',  activeBorder: 'rgba(239,68,68,0.55)' },
  depot:   { text: '#60a5fa', border: 'rgba(59,130,246,0.35)', activeBg: 'rgba(59,130,246,0.15)', activeBorder: 'rgba(59,130,246,0.55)' },
  pending: { text: '#fb7185', border: 'rgba(244,63,94,0.35)',  activeBg: 'rgba(244,63,94,0.15)',  activeBorder: 'rgba(244,63,94,0.55)' },
};

const FilterChip = ({ active, colors, icon, label, count, onClick, title }) => (
  <button
    type="button"
    className={`fr-chip${active ? ' fr-chip-active' : ''}`}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      color: colors.text,
      borderColor: active ? colors.activeBorder : colors.border,
      ...(active ? { background: colors.activeBg } : null),
    }}
    onClick={onClick}
    title={title}
  >
    {icon}
    <span>{label}{count ? ` (${count})` : ''}</span>
  </button>
);

const SectionHead = ({ eyebrow, badge }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, margin: '0 0 12px' }}>
    <span style={S.eyebrow}>{eyebrow}</span>
    {badge != null && badge !== '' && (
      <span className="fr-card-date-end" style={{ fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{badge}</span>
    )}
  </div>
);

/* ── Mobile record card — 4 rows ──
   Row 1: Client + repair badge (if Condition = Repair) + delete icon on the right
   Row 2: Item
   Row 3: Lead Status / Unit Status
   Row 4: Lead Due / Unit Due */
const RecordCard = ({ r, onRowClick, onDelete }) => (
  <div
    className="fr-card tntm-card"
    onClick={() => onRowClick(r.record)}
    style={{ cursor: 'pointer', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 12, padding: 14 }}
  >
    {/* Row 1 — Client, repair badge + delete icon right */}
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
      <div style={{ minWidth: 0 }}>
        <span style={S.fieldLabel}>Client</span>
        <div style={S.blockRowLabel}>{r.client || '—'}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {isRepairCond(r.condition) && (
          <span className="tntm-repair" title="Condition: Repair">
            <WrenchIcon />
          </span>
        )}
        {r.pending ? (
          <button
            type="button"
            className="tntm-pendel"
            title="Pending delete — enter the authorisation code"
            onClick={(e) => { e.stopPropagation(); onDelete(r.record); }}
          >
            <HourglassIcon size={15} />
          </button>
        ) : (
          <button
            type="button"
            className="tntm-del"
            title="Delete record"
            onClick={(e) => { e.stopPropagation(); onDelete(r.record); }}
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </div>

    {/* Row 2 — Item */}
    <div>
      <span style={S.fieldLabel}>Item</span>
      <div className="fr-card-value" style={{ fontSize: '0.9rem' }}>{r.item || '—'}</div>
    </div>

    {/* Row 3 — statuses */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <div>
        <span style={S.fieldLabel}>Lead Status</span>
        <StatusBadge status={r.leadStatus} />
      </div>
      <div>
        <span style={S.fieldLabel}>Unit Status</span>
        <StatusBadge status={r.unitStatus} />
      </div>
    </div>

    {/* Row 4 — due dates */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <div>
        <span style={S.fieldLabel}>Lead Due</span>
        <div className="fr-card-value" style={{ fontSize: '0.88rem' }}>{r.leadDue || '—'}</div>
      </div>
      <div>
        <span style={S.fieldLabel}>Unit Due</span>
        <div className="fr-card-value" style={{ fontSize: '0.88rem' }}>{r.unitDue || '—'}</div>
      </div>
    </div>
  </div>
);

/* ── Shared form fields ── */
const TextField = ({ label, value, onChange, type = 'text', list }) => (
  <div style={{ marginBottom: 10 }}>
    <label style={S.fieldLabel}>{label}</label>
    <input
      type={type}
      list={list}
      style={{ ...S.input, ...(type === 'date' ? { colorScheme: 'dark' } : null) }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const REQUIRED = [
  ['client', 'Client'], ['item', 'Item'], ['serialNum', 'Serial Number'],
  ['condition', 'Condition'], ['passFail', 'Pass/Fail'], ['itemCode', 'Item Code'],
  ['licenceNumber', 'Licence Number'], ['leadTag', 'Lead Tag'], ['unitTag', 'Unit Tag'],
  ['inspectLeadDate', 'Lead Inspection'], ['inspectUnitDate', 'Unit Inspection'],
];
const findMissing = (form) => REQUIRED.filter(([k]) => !String(form[k] ?? '').trim()).map(([, label]) => label);

/* ── Two-tier site autocomplete (new record form) ──
   Options come from the Sites table: name on the first line,
   address underneath in a smaller font. Tapping fills Client
   with the site name. */
const SiteAutocomplete = ({ label, value, onChange, sites }) => {
  const [open, setOpen] = useState(false);
  const q = String(value ?? '').trim().toLowerCase();
  const matches = (q
    ? sites.filter((s) =>
        String(s.name ?? '').toLowerCase().includes(q) ||
        String(s.address ?? '').toLowerCase().includes(q))
    : sites
  ).slice(0, 40);

  return (
    <div style={{ marginBottom: 10, position: 'relative' }}>
      <label style={S.fieldLabel}>{label}</label>
      <input
        type="text"
        autoComplete="off"
        style={S.input}
        value={value}
        placeholder="Start typing a site name or address…"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      />
      {open && matches.length > 0 && (
        <div style={S.siteDrop}>
          {matches.map((s, i) => (
            <div
              key={`${s.name}|${s.address}|${i}`}
              className="tntm-site-opt"
              /* mousedown/touch fires before the input's blur, so the pick lands */
              onMouseDown={(e) => { e.preventDefault(); onChange(s.name); setOpen(false); }}
            >
              <div style={S.siteName}>{s.name}</div>
              {s.address ? <div style={S.siteAddr}>{s.address}</div> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FormBody = ({ form, set, clients, itemsList, showComments = false, sites = null }) => (
  <>
    {sites ? (
      <SiteAutocomplete label="Client" value={form.client} onChange={set('client')} sites={sites} />
    ) : (
      <>
        <TextField label="Client" value={form.client} onChange={set('client')} list="tntm-client-list" />
        <datalist id="tntm-client-list">{clients.map((c) => <option key={c} value={c} />)}</datalist>
      </>
    )}

    <div style={S.row2}>
      <TextField label="Item" value={form.item} onChange={set('item')} list="tntm-item-list" />
      <TextField label="Serial Number" value={form.serialNum} onChange={set('serialNum')} />
    </div>
    <datalist id="tntm-item-list">{itemsList.map((c) => <option key={c} value={c} />)}</datalist>

    <div style={S.row2}>
      <div style={{ marginBottom: 10 }}>
        <CustomSelect label="Condition" value={form.condition} onChange={set('condition')} options={CONDITIONS} />
      </div>
      <div style={{ marginBottom: 10 }}>
        <CustomSelect label="Pass/Fail" value={form.passFail} onChange={set('passFail')} options={PASS_FAIL} />
      </div>
    </div>

    {showComments && (
      <div style={{ marginBottom: 10 }}>
        <label style={S.fieldLabel}>Comments</label>
        <textarea
          style={S.textarea}
          value={form.comments}
          onChange={(e) => set('comments')(e.target.value)}
          placeholder="Repair notes, faults found, parts needed…"
        />
      </div>
    )}

    <div style={S.row2}>
      <div style={{ marginBottom: 10 }}>
        <CustomSelect label="Item Code" value={form.itemCode} onChange={set('itemCode')} options={ITEM_CODES} />
      </div>
      <TextField label="Licence Number" value={form.licenceNumber} onChange={set('licenceNumber')} />
    </div>

    <div style={S.row2}>
      <TextField label="Lead Tag" value={form.leadTag} onChange={set('leadTag')} />
      <TextField label="Unit Tag" value={form.unitTag} onChange={set('unitTag')} />
    </div>

    <div style={S.groupHead}>Inspection Dates</div>
    <div style={S.row2}>
      <TextField label="Lead Inspection" type="date" value={form.inspectLeadDate} onChange={set('inspectLeadDate')} />
      <TextField label="Unit Inspection" type="date" value={form.inspectUnitDate} onChange={set('inspectUnitDate')} />
    </div>
  </>
);

/* ── Change Client picker (edit form) ──
   Popup over the edit modal listing active sites from get_active_clients()
   (delivered as `sites` on /api/testntag). Two-tier rows: site name on top,
   address underneath in a smaller font. Tapping one fills the Client field
   on the edit form and closes the popup. */
const SitePickerModal = ({ open, onClose, sites, onPick }) => {
  const [q, setQ] = useState('');
  if (!open) return null;

  const close = () => { setQ(''); onClose(); };
  const pick = (s) => { setQ(''); onPick(s); };

  const query = q.trim().toLowerCase();
  const matches = query
    ? sites.filter((s) =>
        String(s.name ?? '').toLowerCase().includes(query) ||
        String(s.address ?? '').toLowerCase().includes(query))
    : sites;

  return (
    <div style={{ ...S.modalBackdrop, zIndex: 1400 }} onClick={close}>
      <div style={{ ...S.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />
        <div style={S.modalHead}>
          <h4 style={S.modalTitle}>Change Client</h4>
          <button type="button" style={S.closeX} onClick={close} aria-label="Close">×</button>
        </div>

        {/* Quick pick — sets Client to "Depot" without searching */}
        <button
          type="button"
          className="tntm-depot-btn"
          onClick={() => pick({ name: DEPOT, address: '' })}
        >
          <HomeIcon size={14} />
          <span>Depot</span>
        </button>

        <input
          type="text"
          autoComplete="off"
          style={S.input}
          value={q}
          placeholder="Search site name or address…"
          onChange={(e) => setQ(e.target.value)}
        />
        <div style={{ maxHeight: 300, overflowY: 'auto', marginTop: 10, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, WebkitOverflowScrolling: 'touch' }}>
          {matches.length ? matches.map((s, i) => (
            <div key={`${s.name}|${s.address}|${i}`} className="tntm-site-opt" onClick={() => pick(s)}>
              <div style={S.siteName}>{s.name}</div>
              {s.address ? <div style={S.siteAddr}>{s.address}</div> : null}
            </div>
          )) : (
            <div style={{ padding: '14px 12px', fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>
              {sites.length ? 'No matching sites.' : 'No sites loaded.'}
            </div>
          )}
        </div>
        <button type="button" style={{ ...S.closeBtn, marginTop: 12 }} onClick={close}>Close</button>
      </div>
    </div>
  );
};

/* ── Edit modal (tap a card) ── */
const EditModal = ({ item, onClose, onSubmit, isSubmitting, clients, itemsList, sites }) => {
  const [form, setForm] = useState({
    client: item[F.client] || '',
    licence: item[F.licence] || '',
    item: item[F.item] || '',
    itemCode: item[F.itemCode] || 'UNIT',
    condition: item[F.condition] || 'Good',
    licenceNumber: item[F.licenceNumber] || '',
    leadTag: item[F.leadTag] || '',
    unitTag: item[F.unitTag] || '',
    serialNum: item[F.serialNum] || '',
    passFail: item[F.passFail] || 'Pass',
    inspectLeadDate: dmyToISO(item[F.inspectLead]),
    inspectUnitDate: dmyToISO(item[F.inspectUnit]),
    comments: item[F.comments] || '',
  });
  const [error, setError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = () => {
    const missing = findMissing(form);
    if (missing.length) { setError(`Required: ${missing.join(', ')}`); return; }
    setError('');
    onSubmit({ id: item[F.id], ...form });
  };

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />
        <div style={S.modalHead}>
          <h4 style={S.modalTitle}>Edit TestNTag Item</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={{ marginBottom: 10 }}>
          <label style={S.fieldLabel}>Licence</label>
          <div style={S.readOnly}>{form.licence || '—'}</div>
        </div>

        <FormBody form={form} set={set} clients={clients} itemsList={itemsList} showComments />

        {error && <div style={S.errText}>{error}</div>}
        <div style={S.row2}>
          <button type="button" style={{ ...S.submitBtn, ...(isSubmitting ? { opacity: 0.6, cursor: 'wait' } : null) }} disabled={isSubmitting} onClick={submit}>
            {isSubmitting ? 'Saving…' : 'Save Changes'}
          </button>
          <button type="button" style={S.altBtn} onClick={() => setPickerOpen(true)}>
            Change Client
          </button>
        </div>
        <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>

        <SitePickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          sites={sites || []}
          onPick={(s) => { set('client')(s.name); setPickerOpen(false); }}
        />
      </div>
    </div>
  );
};

/* ── New record modal ── */
const NewRecordModal = ({ open, onClose, userName, sites, itemsList, onCreated, notify }) => {
  const blank = {
    client: '', item: '', serialNum: '', condition: 'Good', passFail: 'Pass',
    itemCode: 'UNIT', licenceNumber: '', leadTag: '', unitTag: '',
    inspectLeadDate: '', inspectUnitDate: '',
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  if (!open) return null;

  const submit = async () => {
    const missing = findMissing(form);
    if (missing.length) { setError(`Required: ${missing.join(', ')}`); return; }
    setError('');
    try {
      setBusy(true);
      await createTestNTag({ ...form, licence: userName });
      notify({ message: 'TestNTag record created.', type: 'success' });
      setForm(blank);
      onCreated();
    } catch (e) {
      notify({ message: e.message || 'Failed to create record', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />
        <div style={S.modalHead}>
          <h4 style={S.modalTitle}>New TestNTag Item</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Licence captured from the logged-in session user */}
        <div style={{ marginBottom: 10 }}>
          <label style={S.fieldLabel}>Licence (you)</label>
          <div style={S.readOnly}>{userName || '—'}</div>
        </div>

        <FormBody form={form} set={set} sites={sites} itemsList={itemsList} />

        {error && <div style={S.errText}>{error}</div>}
        <button type="button" style={{ ...S.submitBtn, ...(busy ? { opacity: 0.6, cursor: 'wait' } : null) }} disabled={busy} onClick={submit}>
          {busy ? 'Submitting…' : 'Submit Job'}
        </button>
        <button type="button" style={S.closeBtn} onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

/* ── Item Codes reference modal (read-only, horizontal scroll on mobile) ── */
const IC_COLS = '1.1fr 1.1fr 1.6fr 1.6fr 2.2fr 0.7fr';

const ItemCodesModal = ({ open, onClose }) => {
  if (!open) return null;
  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 820, overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />
        <div style={S.modalHead}>
          <h4 style={S.modalTitle}>Item Codes</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', margin: '0 -4px' }}>
          <div style={{ minWidth: 680, padding: '0 4px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: IC_COLS, gap: 8, alignItems: 'end', padding: '0 8px 10px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
              <span style={S.blockColHead}>Name</span>
              <span style={S.blockColHead}>Item Code</span>
              <span style={S.blockColHead}>Lead Due Days</span>
              <span style={S.blockColHead}>Unit Due Days</span>
              <span style={S.blockColHead}>Description</span>
              <span style={{ ...S.blockColHead, textAlign: 'right' }}>Items</span>
            </div>
            {ITEM_CODE_INFO.map((r, i) => (
              <div
                key={r.code}
                style={{
                  display: 'grid', gridTemplateColumns: IC_COLS, gap: 8, alignItems: 'center',
                  padding: '9px 8px', borderTop: i ? '1px solid rgba(255,255,255,0.06)' : 'none',
                }}
              >
                <span style={S.blockRowLabel}>{r.name}</span>
                <span className="fr-card-value" style={S.blockValue}>{r.code}</span>
                <span className="fr-card-value" style={{ ...S.blockValue, whiteSpace: 'normal' }}>{r.lead}</span>
                <span className="fr-card-value" style={{ ...S.blockValue, whiteSpace: 'normal' }}>{r.unit}</span>
                <span className="fr-card-value" style={{ ...S.blockValue, whiteSpace: 'normal' }}>{r.desc}</span>
                <span className="fr-card-value" style={{ ...S.blockValue, textAlign: 'right' }}>{r.num}</span>
              </div>
            ))}
          </div>
        </div>

        <button type="button" style={{ ...S.closeBtn, marginTop: 12 }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

/* ── Delete authorisation modal ──
   Replaces the instant delete. Two paths:
   • "Request Authorization For Delete" — the backend generates a random
     6-digit code, PATCHes it onto the record's "Authorise Delete" field and
     emails it (with the record details) to Bill + CCs. The record then shows
     as Pending Delete until authorised.
   • "Authorise Delete" — the entered 6-digit code is checked server-side
     against the record's "Authorise Delete" field; on a match the record
     is deleted. */
const DeleteAuthModal = ({ record, onClose, notify, onDone }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState('');       // '' | 'auth' | 'request'
  const [error, setError] = useState('');
  const pending = isPendingCode(record[F.authDelete]);

  const authorise = async () => {
    const c = code.trim();
    if (!/^\d{6}$/.test(c)) { setError('Enter the 6 digit authorisation code.'); return; }
    setError('');
    try {
      setBusy('auth');
      await authoriseDeleteTestNTag(record[F.id], c);
      notify({ message: 'Record deleted.', type: 'success' });
      await onDone();
    } catch (e) {
      setError(e.message || 'Authorisation failed');
    } finally {
      setBusy('');
    }
  };

  const requestAuth = async () => {
    setError('');
    try {
      setBusy('request');
      await requestDeleteAuthTestNTag(record[F.id]);
      notify({ message: 'Authorisation requested — the code has been emailed for approval.', type: 'success' });
      await onDone();
    } catch (e) {
      setError(e.message || 'Request failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div style={S.modalBackdrop} onClick={busy ? undefined : onClose}>
      <div style={{ ...S.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={S.panelAccent} />
        <div style={S.modalHead}>
          <h4 style={S.modalTitle}>Delete Authorisation</h4>
          <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={S.fieldLabel}>Record</label>
          <div style={S.readOnly}>
            {record[F.item] || '—'} — {record[F.client] || '—'}
            <div style={{ fontSize: '0.72rem', fontWeight: 400, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
              Serial: {record[F.serialNum] || '—'}
            </div>
          </div>
        </div>

        {pending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#fb7185', fontSize: '0.78rem', margin: '0 2px 10px' }}>
            <HourglassIcon size={13} />
            <span>An authorisation code has already been requested for this record.</span>
          </div>
        )}

        <div style={{ marginBottom: 10 }}>
          <label style={S.fieldLabel}>Authorise Code (6 digits)</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="••••••"
            style={S.codeInput}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
        </div>

        {error && <div style={S.errText}>{error}</div>}

        <button
          type="button"
          style={{ ...S.dangerBtn, ...(busy ? { opacity: 0.6, cursor: 'wait' } : null) }}
          disabled={!!busy}
          onClick={authorise}
        >
          {busy === 'auth' ? 'Deleting…' : 'Authorise Delete'}
        </button>
        <button
          type="button"
          style={{ ...S.requestBtn, ...(busy ? { opacity: 0.6, cursor: 'wait' } : null) }}
          disabled={!!busy}
          onClick={requestAuth}
        >
          {busy === 'request' ? 'Sending request…' : 'Request Authorization For Delete'}
        </button>
        <button type="button" style={{ ...S.closeBtn, marginTop: 6 }} onClick={onClose} disabled={!!busy}>Close</button>
      </div>
    </div>
  );
};

/* ── Toolbar actions ── */
const ExcelAction = ({ rows, setToast }) => {
  const [busy, setBusy] = useState(false);
  const onClick = async () => {
    if (!rows.length) {
      setToast({ message: 'Nothing to download — no records in the current filter.', type: 'error' });
      return;
    }
    try {
      setBusy(true);
      const blob = await downloadTestNTagExcel(rows);
      downloadBlob(blob, 'testntag_records.xlsx');
    } catch (e) {
      setToast({ message: e.message || 'Download failed', type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="fr-download-btn" onClick={onClick} disabled={busy} title="Download filtered records (Excel)" style={busy ? { opacity: 0.5 } : undefined}>
      <SheetIcon />
    </button>
  );
};

const NewAction = ({ onOpen }) => (
  <button type="button" className="fr-download-btn" onClick={onOpen} title="New TestNTag record">
    <PlusIcon />
  </button>
);

const ItemCodesAction = ({ onOpen }) => (
  <button type="button" className="fr-download-btn" onClick={onOpen} title="Item Codes">
    <InfoIcon />
  </button>
);

/* Reassign — sits next to the ? help button, opens ReassigningModalTestnTag */
const ReassignAction = ({ onOpen }) => (
  <button type="button" className="fr-download-btn" onClick={onOpen} title="Reassign records to another staff member">
    <ReassignIcon />
  </button>
);

/* ── Page (mobile — used inside InnerTabsm) ── */
const TestNTagm = ({ showHeader = false }) => {
  const { user } = useUser();
  const userName = user?.userName || '';

  const [visible, setVisible] = useState([]);   // filtered rows (drives cards + excel)
  const [allItems, setAllItems] = useState([]); // full dataset (drives chips + autocomplete)
  const [sites, setSites] = useState([]);       // Sites table [{name, address}] for the new-record form
  const [typed, setTyped] = useState('');       // mirror of the search input
  const [newOpen, setNewOpen] = useState(false);
  const [itemCodesOpen, setItemCodesOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);

  const refetchRef = useRef(null);
  const setTypedQueryRef = useRef(null);
  const toastRef = useRef(null);

  const notify = useCallback((t) => { if (toastRef.current) toastRef.current(t); }, []);

  /* Wrap the API call so we can capture `sites` from the same response
     FilterTemplate consumes — no second request needed. Also appends a
     synthetic column (index 19) flagging records whose "Authorise Delete"
     field holds a 6-digit code, so the Pending Delete chip can exact-match. */
  const fetchData = useCallback(async () => {
    const data = await fetchTestNTag();
    setSites(Array.isArray(data?.sites) ? data.sites : []);
    if (Array.isArray(data?.testntag)) {
      data.testntag = data.testntag.map((r) => {
        const row = [...r];
        row[F.pendingFlag] = isPendingCode(row[F.authDelete]) ? PENDING : '';
        return row;
      });
    }
    return data;
  }, []);

  /* Autocomplete pools from the live dataset (client / serial num / item) */
  const clients = useMemo(
    () => [...new Set(allItems.map((r) => r[F.client]).filter(Boolean))].sort(),
    [allItems]
  );
  const serials = useMemo(
    () => [...new Set(allItems.map((r) => r[F.serialNum]).filter(Boolean))].sort(),
    [allItems]
  );
  const itemsList = useMemo(
    () => [...new Set(allItems.map((r) => r[F.item]).filter(Boolean))].sort(),
    [allItems]
  );

  /* Suggestions shown under the search — tap one to complete the search */
  const suggestions = useMemo(() => {
    const q = typed.trim().toLowerCase();
    if (!q) return [];
    const pool = new Set();
    [...clients, ...serials, ...itemsList].forEach((v) => {
      const s = String(v);
      if (s.toLowerCase().includes(q) && s.toLowerCase() !== q) pool.add(s);
    });
    return [...pool].sort().slice(0, 8);
  }, [typed, clients, serials, itemsList]);

  /* Dynamic user chips from Licence (index 6) — one per user, always shown */
  const userChips = useMemo(() => {
    const seen = new Map();
    allItems.forEach((r) => {
      const lic = String(r[F.licence] ?? '').trim();
      if (lic && !seen.has(lic)) seen.set(lic, firstName(lic));
    });
    return [...seen.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allItems]);

  /* Chip totals — Ok/Overdue count records where either the lead or unit
     status matches (same OR semantics as the chip filter) */
  const repairCount = useMemo(
    () => allItems.filter((r) => isRepairCond(r[F.condition])).length,
    [allItems]
  );
  const okCount = useMemo(
    () => allItems.filter((r) => eqI(r[F.leadStatus], OK) || eqI(r[F.unitStatus], OK)).length,
    [allItems]
  );
  const overdueCount = useMemo(
    () => allItems.filter((r) => eqI(r[F.leadStatus], OVERDUE) || eqI(r[F.unitStatus], OVERDUE)).length,
    [allItems]
  );
  const depotCount = useMemo(
    () => allItems.filter((r) => eqI(r[F.client], DEPOT)).length,
    [allItems]
  );
  const pendingCount = useMemo(
    () => allItems.filter((r) => isPendingCode(r[F.authDelete])).length,
    [allItems]
  );

  /* Delete icon now opens the authorisation modal instead of deleting */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const handleDelete = useCallback((record) => setDeleteTarget(record), []);

  /* Chips row — info bar above stays exactly as FilterTemplate renders it */
  const renderChips = useCallback((chipQuery, setChipQuery) => {
    return (
      <div>
        {suggestions.length > 0 && (
          <div style={S.suggestWrap}>
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                style={S.suggestPill}
                onClick={() => setTypedQueryRef.current && setTypedQueryRef.current(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="fr-filter-chips">
          {userChips.map((c) => {
            const active = chipQuery === c.value;
            return (
              <button
                key={c.value}
                type="button"
                className={`fr-chip${active ? ' fr-chip-active' : ''}`}
                style={active ? { background: 'rgba(34,211,238,0.15)', color: '#22d3ee', borderColor: 'rgba(34,211,238,0.5)' } : undefined}
                onClick={() => setChipQuery(active ? '' : c.value)}
                title={c.value}
              >
                {c.label}
              </button>
            );
          })}
          {/* Filter chips — Repair (condition), Ok / Overdue (lead OR unit
              status), Depot (client). Each is an exact-match chip filter. */}
          <FilterChip
            active={chipQuery === REPAIR}
            colors={CHIP_COLORS.repair}
            icon={<WrenchIcon size={12} />}
            label="Repair"
            count={repairCount}
            onClick={() => setChipQuery(chipQuery === REPAIR ? '' : REPAIR)}
            title="Show items marked as Repair"
          />
          <FilterChip
            active={chipQuery === OK}
            colors={CHIP_COLORS.ok}
            icon={<CheckCircleIcon />}
            label="Ok"
            count={okCount}
            onClick={() => setChipQuery(chipQuery === OK ? '' : OK)}
            title="Show items with an Ok lead or unit status"
          />
          <FilterChip
            active={chipQuery === OVERDUE}
            colors={CHIP_COLORS.overdue}
            icon={<ClockIcon />}
            label="Overdue"
            count={overdueCount}
            onClick={() => setChipQuery(chipQuery === OVERDUE ? '' : OVERDUE)}
            title="Show items with an overdue lead or unit status"
          />
          <FilterChip
            active={chipQuery === DEPOT}
            colors={CHIP_COLORS.depot}
            icon={<HomeIcon />}
            label="Depot"
            count={depotCount}
            onClick={() => setChipQuery(chipQuery === DEPOT ? '' : DEPOT)}
            title="Show items whose client is Depot"
          />
          <FilterChip
            active={chipQuery === PENDING}
            colors={CHIP_COLORS.pending}
            icon={<HourglassIcon />}
            label="Pending Delete"
            count={pendingCount}
            onClick={() => setChipQuery(chipQuery === PENDING ? '' : PENDING)}
            title="Show records awaiting delete authorisation"
          />
          {chipQuery && (
            <button type="button" style={S.link} onClick={() => setChipQuery('')}>Clear filter</button>
          )}
        </div>
      </div>
    );
  }, [userChips, suggestions, repairCount, okCount, overdueCount, depotCount, pendingCount]);

  /* Cards instead of a table — header block rendered once for the first visible row */
  const renderCard = useCallback((item, onCardClick) => {
    if (!visible.length) return null;
    const isFirst = String(visible[0][F.id]) === String(item[F.id]);

    const r = {
      id: item[F.id],
      record: item,
      client: item[F.client],
      item: item[F.item],
      leadStatus: item[F.leadStatus],
      unitStatus: item[F.unitStatus],
      leadDue: item[F.leadDue],
      unitDue: item[F.unitDue],
      condition: item[F.condition],
      pending: item[F.pendingFlag] === PENDING,
    };

    return (
      <>
        {isFirst && (
          <SectionHead
            eyebrow="Test & Tag Register"
            badge={`${visible.length} item${visible.length === 1 ? '' : 's'}`}
          />
        )}
        <RecordCard r={r} onRowClick={onCardClick} onDelete={handleDelete} />
      </>
    );
  }, [visible, handleDelete]);

  const renderModal = useCallback(
    (item, onClose, handleFormSubmit, isSubmitting) => (
      <EditModal
        item={item}
        onClose={onClose}
        onSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        clients={clients}
        itemsList={itemsList}
        sites={sites}
      />
    ),
    [clients, itemsList, sites]
  );

  const handleUpdate = useCallback(async (data) => {
    const { id, ...payload } = data;
    return updateTestNTag(id, payload);
  }, []);

  const renderExtraActions = useCallback(({ setToast }) => {
    toastRef.current = setToast; // capture the template's toast for delete / create / reassign
    return (
      <>
        <NewAction onOpen={() => setNewOpen(true)} />
        <ExcelAction rows={visible} setToast={setToast} />
        <ItemCodesAction onOpen={() => setItemCodesOpen(true)} />
        <ReassignAction onOpen={() => setReassignOpen(true)} />
      </>
    );
  }, [visible]);

  return (
    <>
      <style>{`
        .tntm-card { transition: background .12s ease, border-color .12s ease; }
        .tntm-card:active { background: rgba(34,211,238,0.07); }
        .tntm-del {
          background: rgba(239,68,68,0.1);
          border: 1px solid rgba(239,68,68,0.35);
          color: #f87171;
          border-radius: 8px;
          width: 32px; height: 32px;
          flex-shrink: 0;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: background .12s ease;
        }
        .tntm-del:active { background: rgba(239,68,68,0.25); }
        .tntm-pendel {
          background: rgba(244,63,94,0.1);
          border: 1px solid rgba(244,63,94,0.4);
          color: #fb7185;
          border-radius: 8px;
          width: 32px; height: 32px;
          flex-shrink: 0;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: background .12s ease;
        }
        .tntm-pendel:active { background: rgba(244,63,94,0.22); }
        .tntm-repair {
          background: rgba(245,158,11,0.12);
          border: 1px solid rgba(245,158,11,0.4);
          color: #fbbf24;
          border-radius: 8px;
          width: 32px; height: 32px;
          flex-shrink: 0;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .tntm-site-opt {
          padding: 10px 12px;
          cursor: pointer;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          transition: background .12s ease;
        }
        .tntm-site-opt:last-child { border-bottom: none; }
        .tntm-site-opt:active { background: rgba(34,211,238,0.1); }
        .tntm-depot-btn {
          width: 100%;
          box-sizing: border-box;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 11px;
          margin-bottom: 10px;
          border-radius: 10px;
          border: 1px solid rgba(59,130,246,0.45);
          background: rgba(59,130,246,0.12);
          color: #60a5fa;
          font-weight: 700;
          font-size: 0.88rem;
          cursor: pointer;
          transition: background .12s ease;
        }
        .tntm-depot-btn:active { background: rgba(59,130,246,0.22); }

        /* ── Info bar → two rows (mobile only, scoped to .tntm-wrap) ──
           Row 1: "TestNTag · Total / Filtered" text
           Row 2: the 5 action icons (help → reassign), starting at the left
                  and spreading across the full width.
           Targets FilterTemplate's DOM via :has() so filterTemplate.css and
           the desktop page are untouched. */

        /* The info bar itself: the div whose grandchild is an action button.
           Let its children wrap onto a second line. */
        .tntm-wrap div:has(> div > .fr-download-btn) {
          display: flex !important;
          flex-wrap: wrap !important;
          align-items: center;
          row-gap: 10px;
        }

        /* The actions container: the div that directly holds the icon
           buttons. Push it to its own full-width second row and spread the
           5 buttons from the left edge across the bar. */
        .tntm-wrap div:has(> .fr-download-btn) {
          order: 2;
          flex: 1 1 100% !important;
          min-width: 100%;
          display: flex !important;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          margin: 0 !important;
        }

        /* Everything else in the bar (the title + Total / Filtered text)
           stays on row 1, taking the full first line. */
        .tntm-wrap div:has(> div > .fr-download-btn) > *:not(:has(> .fr-download-btn)) {
          flex: 1 1 auto;
          min-width: 0;
        }
      `}</style>

      {/* Outer spacing + clearance above FooterNav (68px bar + safe area).
          InnerTabsm's content area handles the vertical scrolling.
          `tntm-wrap` scopes the two-row info-bar override to this page only. */}
      <div className="tntm-wrap" style={{ padding: '12px 12px calc(88px + env(safe-area-inset-bottom, 0px))', boxSizing: 'border-box' }}>
        <FilterTemplate
          title="TestNTag"
          subtitle="TestNTag"
          showHeader={showHeader}
          showNavigation={false}
          stylesheetHref={filterTemplateStyles}
          fetchData={fetchData}
          dataKey="testntag"
          searchFields={[F.licence, F.condition, F.leadStatus, F.unitStatus, F.client, F.pendingFlag]}  /* chips: user / Repair / Ok / Overdue / Depot / Pending Delete */
          typingSearchFields={[F.client, F.serialNum, F.item]}    /* typed search: client / serial / item */
          searchPlaceholder="Search client, serial number or item…"
          helpButton={<HelpButton helpData={[HELP]} />}
          renderChips={renderChips}
          renderCard={renderCard}
          renderModal={renderModal}
          onSubmit={handleUpdate}
          renderExtraActions={renderExtraActions}
          onFilteredChange={setVisible}
          onItemsChange={setAllItems}
          onTypedQueryChange={setTyped}
          onSetTypedQueryRef={setTypedQueryRef}
          onRefetchRef={refetchRef}
          emptyIcon="🔌"
          emptyTitle="No TestNTag Records"
          loadingMessage="Loading TestNTag records…"
        />
      </div>

      <ItemCodesModal open={itemCodesOpen} onClose={() => setItemCodesOpen(false)} />

      {deleteTarget && (
        <DeleteAuthModal
          record={deleteTarget}
          notify={notify}
          onClose={() => setDeleteTarget(null)}
          onDone={async () => {
            setDeleteTarget(null);
            if (refetchRef.current) await refetchRef.current();
          }}
        />
      )}

      {reassignOpen && (
        <ReassigningModalTestnTag
          rows={visible}
          allRows={allItems}
          notify={notify}
          onClose={() => setReassignOpen(false)}
          onSaved={async () => {
            if (refetchRef.current) await refetchRef.current();
          }}
        />
      )}

      <NewRecordModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        userName={userName}
        sites={sites}
        itemsList={itemsList}
        notify={notify}
        onCreated={async () => {
          setNewOpen(false);
          if (refetchRef.current) await refetchRef.current();
        }}
      />
    </>
  );
};

export default TestNTagm;